import React, { useState, useEffect, ChangeEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatDate, formatMonth } from '@/utils/format';
import type { FlatRateAgreement, Applicant, Consumer } from "../types";
import { InputFieldBox } from "@/components/InputField";
import { PasswordFieldBox } from "@/components/PasswordField";
import { RadioFieldBox } from "@/components/RadioField";
import { DateFieldBox } from "@/components/DateField";
import { AddressFieldBox } from "@/components/AddressField";
import { PhoneFieldBox } from "@/components/PhoneField";
import InfoDisplay from "@/components/InfoDisplay";
import { SelectInputBox } from "@/components/SelectInput";
import SidebarNavigation from "@/components/SidebarNavigation";
import Layout from "../../shared/components/Layout";
import AlertMessages from "@/components/AlertMessages";
import getRegistration from "../api/getRegistration";
import { fetchAddressData } from "@/utils/format";
import type OccupationLabels from "../../shared/types/OccupationLabels";
import confirmRegistration from "../api/confirmRegistration";
import type { RegistrationParams } from "../types";
import { useFlatRateContext } from "../../shared/providers/FlatRateProvider";
import clearLocalStorage from "@/utils/clearLocalStorage";

const Registration: React.FC = () => {
  const displayPlan = (plan: string, price: string): string => {
    if(plan === 'both') return `両眼プラン(${price}円/月)`
    if(plan === 'right') return `片眼プラン(右眼)(${price}円/月)`
    if(plan === 'left') return `片眼プラン(左眼)(${price}円/月)`
    return '';
  }

  const navigate = useNavigate();
  const location = useLocation();

  const { passwordValues, setPasswordValues, alert, setAlert } = useFlatRateContext();

  const [customer, setCustomer] = useState({
    custCode: "",
    email: "",
  });

  const [flatRateAgreement, setFlatRateAgreement] = useState<FlatRateAgreement>();
  const [applicant, setApplicant] = useState<Applicant>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    sex: "",
    stateId: "",
    zipcode: "",
  });
  const [consumer, setConsumer] = useState<Consumer>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    relationship: "",
    sex: "",
    stateId: "",
    zipcode: "",
    isSameApplicant: true,
    isSameAddress: true,
    isSamePhone: true
  });
  
  const [states, setStates] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRegistration();
        if (response.data.flatRateAgreement.status !== "before_confirmation") navigate("/customer/top");
        setCustomer(response.data.customer);
        setFlatRateAgreement(response.data.flatRateAgreement);
        setApplicant(response.data.applicant);
        setConsumer(prevConsumer => ({...prevConsumer, ...response.data.consumer}));
        setStates(response.data.states);

        const fromConfirmPage = location.state?.fromConfirmPage;
        if(fromConfirmPage) {
          const storedCustomer = localStorage.getItem("customer");
          if(storedCustomer) {
            setCustomer(JSON.parse(storedCustomer));
          }

          const storedApplicant = localStorage.getItem("applicant");
          if(storedApplicant) {
            setApplicant(JSON.parse(storedApplicant));
          }

          const storedConsumer = localStorage.getItem("consumer");
          if(storedConsumer) {
            setConsumer(JSON.parse(storedConsumer));
          }
        } else {
          clearLocalStorage();
        }

      } catch (error) {
        console.error("Error fetching flat rate lens data:", error);
      }
    };

    fetchData();
  }, [location.state?.fromConfirmPage, navigate]);

  const applicantSexOptions = [
    { checked: applicant.sex === "male" || applicant.sex === null, label: "男性", onChange: () => setApplicant({...applicant, sex: "male" }) },
    { checked: applicant.sex === "female", label: "女性", onChange: () => setApplicant({...applicant, sex: "female" }) },
    { checked: applicant.sex === "unknown", label: "不明", onChange: () => setApplicant({...applicant, sex: "unknown" }) },
  ];

  const consumerSexOptions = [
    { checked: consumer.sex === "male" || consumer.sex === null, label: "男性", onChange: () => setConsumer({...consumer, sex: "male" }) },
    { checked: consumer.sex === "female", label: "女性", onChange: () => setConsumer({...consumer, sex: "female" }) },
    { checked: consumer.sex === "unknown", label: "不明", onChange: () => setConsumer({...consumer, sex: "unknown" }) },
  ];

  const applicantAddressData = {
    zipcode: {
      label: "郵便番号",
      value: applicant.zipcode,
      onChange: ((event: ChangeEvent<HTMLInputElement>) => setApplicant({...applicant, zipcode: event.target.value })),
    },
    stateId: {
      label: "都道府県",
      value: applicant.stateId,
      onChange: ((event: ChangeEvent<HTMLSelectElement>) => setApplicant({...applicant, stateId: event.target.value }))
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: applicant.address1,
      onChange: ((event: ChangeEvent<HTMLInputElement>) => setApplicant({...applicant, address1: event.target.value }))
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: applicant.address2,
      onChange: ((event: ChangeEvent<HTMLInputElement>) => setApplicant({...applicant, address2: event.target.value }))
    },
  }

  const consumerAddressData = {
    zipcode: {
      label: "郵便番号",
      value: consumer.zipcode,
      onChange: ((event: ChangeEvent<HTMLInputElement>) => setConsumer({...consumer, zipcode: event.target.value })),
    },
    stateId: {
      label: "都道府県",
      value: consumer.stateId,
      onChange: ((event: ChangeEvent<HTMLSelectElement>) => setConsumer({...consumer, stateId: event.target.value }))
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: consumer.address1,
      onChange: ((event: ChangeEvent<HTMLInputElement>) => setConsumer({...consumer, address1: event.target.value }))
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: consumer.address2,
      onChange: ((event: ChangeEvent<HTMLInputElement>) => setConsumer({...consumer, address2: event.target.value }))
    },
  }

  const inputApplicantAddressData = async (zipcode: string) => {
    try {
      const data = await fetchAddressData(zipcode);
      if (data.stateId && data.address1) {
        setApplicant((prevApplicant) => ({
          ...prevApplicant,
          stateId: data.stateId,
          address1: data.address1,
        }));
      }
    } catch (error) {
      console.error('住所データの取得に失敗しました:', error);
    }
  };

  const inputConsumerAddressData = async (zipcode: string) => {
    try {
      const data = await fetchAddressData(zipcode);
      if (data.stateId && data.address1) {
        setConsumer((prevConsumer) => ({
          ...prevConsumer,
          stateId: data.stateId,
          address1: data.address1,
        }));
      }
    } catch (error) {
      console.error('住所データの取得に失敗しました:', error);
    }
  };

  const OccupationLabels: OccupationLabels = {
    employee: "会社員・公務員",
    independent: "自営",
    housewife: "主婦",
    student: "学生",
    other: "その他"
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let updatedConsumer = {...consumer};
    if (updatedConsumer.isSameApplicant) {
      updatedConsumer = {
        ...applicant,
        relationship: '本人',
        isSameApplicant: updatedConsumer.isSameApplicant,
        isSameAddress: updatedConsumer.isSameAddress,
        isSamePhone: updatedConsumer.isSamePhone,    
      };
    } else {
      if (updatedConsumer.isSameAddress) {
        updatedConsumer = {
          ...updatedConsumer,
          stateId: applicant.stateId,
          zipcode: applicant.zipcode,
          address1: applicant.address1,
          address2: applicant.address2,
        };
      }
      if (updatedConsumer.isSamePhone) {
        updatedConsumer = {
          ...updatedConsumer,
          homePhone: applicant.homePhone,
          mobilePhone: applicant.mobilePhone,
        };
      }
    }

    const params: RegistrationParams = {
      customer: {...customer, ...passwordValues},
      applicant: applicant,
      consumer: updatedConsumer,
    }

    try {
      const res = await confirmRegistration(params);
      setAlert({open: false, messages: []});
      localStorage.setItem("flatRateAgreement", JSON.stringify(flatRateAgreement));
      localStorage.setItem("customer", JSON.stringify(customer));
      localStorage.setItem("applicant", JSON.stringify(applicant));
      localStorage.setItem("consumer", JSON.stringify(updatedConsumer));
      localStorage.setItem("states", JSON.stringify(states));
      localStorage.setItem("gmoUrl", JSON.stringify(res.data.url));

      navigate("/customer/flat_rates/registration/confirm");
      window.scrollTo(0, 0);
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.errors) {
        setAlert({open: true, messages: err.response.data.errors.fullMessages});
        window.scrollTo(0, 0);
      } else {
        setAlert({open: false, messages: []});
        console.log('An unexpected error occurred:', err);
      }
    }
  }

  return (
    <Layout>
      <main className="wrapper" id="page-facility_login">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">新規契約申込入力</h1>
            <AlertMessages open={alert.open} messages={alert.messages} />
            <div className="contact-box">
              <div className="contact-box__left">
                <div className="mbpc-98 mbsp-32">
                  <form onSubmit={handleSubmit}>
                    <div className="contact-box__form js-item" id="id-01">
                      <h2 className="c-ttl__02">お客様情報</h2>
                      <InfoDisplay label="お客様ID" value={customer.custCode} />
                      <InfoDisplay label="ご契約プラン" value={displayPlan(flatRateAgreement?.plan ?? '', flatRateAgreement?.price?.toString() ?? '')} />
                      <InfoDisplay label="サービス利用開始日" value={formatDate(flatRateAgreement?.startDate ?? '')} />
                      <InfoDisplay label="サービス利用開始月" value={formatMonth(flatRateAgreement?.startMonth ?? '')} />
                      <InputFieldBox
                        label={"メールアドレス"}
                        placeholder={"test@example.com"}
                        value={customer.email}
                        onChange={event => setCustomer({...customer, email: event.target.value })}
                        required={true}
                      />
                      <PasswordFieldBox
                        label={"パスワード"}
                        value={passwordValues.password}
                        onChange={event => setPasswordValues({...passwordValues, password: event.target.value })}
                        required={true}
                      />
                      <PasswordFieldBox
                        label={"パスワード確認"}
                        value={passwordValues.passwordConfirmation}
                        onChange={event => setPasswordValues({...passwordValues, passwordConfirmation: event.target.value })}
                        required={true}
                      />
                    </div>
                    <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                      <h2 className="c-ttl__02">申込者情報<span>※20歳未満の方は、保護者の方が申込者となります。</span></h2>
                      <InputFieldBox
                        label={"申込者名(カナ)"}
                        placeholder={"タナカ イチロウ"}
                        value={applicant.nameKana}
                        onChange={event => setApplicant({...applicant, nameKana: event.target.value })}
                        required={true}
                      />
                      <InputFieldBox
                        label={"申込者名(漢字)"}
                        placeholder={"田中 一郎"}
                        value={applicant.name}
                        onChange={event => setApplicant({...applicant, name: event.target.value })}
                        required={true}
                      />
                      <RadioFieldBox
                        label="申込者性別"
                        list={applicantSexOptions}
                      />
                      <DateFieldBox
                        label={"申込者生年月日"}
                        pattern={"d{4}-d{2}-d{2}"}
                        value={applicant.birthday}
                        onChange={event => setApplicant({...applicant, birthday: event.target.value })}
                        required={true}
                      />
                      <AddressFieldBox
                        label="申込者住所"
                        addressData={applicantAddressData}
                        required={true}
                        states={states}
                        inputAddressData={inputApplicantAddressData}
                      />
                      <PhoneFieldBox
                        label="申込者電話番号"
                        phoneData={{
                          homePhone: {label: "自宅", value: applicant.homePhone, onChange: event => setApplicant({...applicant, homePhone: event.target.value })},
                          mobilePhone: {label: "携帯", value: applicant.mobilePhone, onChange: event => setApplicant({...applicant, mobilePhone: event.target.value })},
                        }}
                        required={true}
                      />
                      <SelectInputBox
                        label="申込者ご職業"
                        required={true}
                        options={Object.entries(OccupationLabels || {}).map(([key, label]) => ({
                          value: key,
                          label: label
                        }))}
                        value={applicant.occupation}
                        onChangeFunctions={[(newValue) => setApplicant({...applicant, occupation: newValue })]}
                      />
                    </div>
                    <div className="contact-box__form mbsp-29 js-item h-adr" id="id-03">
                      <h2 className="c-ttl__02">レンズ使用者情報</h2>
                      <RadioFieldBox
                        label="使用者について"
                        list={[
                            { checked: consumer.isSameApplicant, label: "申込者と同一", onChange: () => setConsumer({...consumer, isSameApplicant: true}) },
                            { checked: !(consumer.isSameApplicant), label: "申込者と異なる", onChange: () => setConsumer({...consumer, isSameApplicant: false}) },
                          ]}
                        itemStart={false}
                      />
                      {!(consumer.isSameApplicant) &&
                        <>
                          <InputFieldBox
                            label={"使用者名(カナ)"}
                            placeholder={"タナカ イチロウ"}
                            value={consumer.nameKana}
                            onChange={event => setConsumer({...consumer, nameKana: event.target.value })}
                            required={true}
                          />
                          <InputFieldBox
                            label={"使用者(漢字)"}
                            placeholder={"田中 一郎"}
                            value={consumer.name}
                            onChange={event => setConsumer({...consumer, name: event.target.value })}
                            required={true}
                          />
                          <InputFieldBox
                            label={"申込者との関係"}
                            placeholder={"子供"}
                            value={consumer.relationship}
                            onChange={event => setConsumer({...consumer, relationship: event.target.value })}
                            required={true}
                          />
                          <RadioFieldBox
                            label="使用者性別"
                            list={consumerSexOptions}
                          />
                          <DateFieldBox
                            label={"使用者生年月日"}
                            pattern={"d{4}-d{2}-d{2}"}
                            value={consumer.birthday}
                            onChange={event => setConsumer({...consumer, birthday: event.target.value })}
                            required={true}
                          />
                          <AddressFieldBox
                            label="使用者住所"
                            addressData={consumerAddressData}
                            required={true}
                            states={states}
                            inputAddressData={inputConsumerAddressData}
                            checkboxData={{label: "申込者と同一", defaultChecked: consumer.isSameAddress, onChange: (event) => {setConsumer({...consumer, isSameAddress: event.target.checked})}}}
                          />
                          <PhoneFieldBox
                            label="使用者電話番号"
                            required={true}
                            checkboxData={{label: "申込者と同一", defaultChecked: consumer.isSamePhone, onChange: (event) => {setConsumer({...consumer, isSamePhone: event.target.checked})}}}
                            phoneData={{
                              homePhone: {label: "自宅", value: consumer.homePhone, onChange: event => setConsumer({...consumer, homePhone: event.target.value })},
                              mobilePhone: {label: "携帯", value: consumer.mobilePhone, onChange: event => setConsumer({...consumer, mobilePhone: event.target.value })},
                            }}
                          />
                          <SelectInputBox
                            label="使用者ご職業"
                            required={true}
                            options={Object.entries(OccupationLabels || {}).map(([key, label]) => ({
                              value: key,
                              label: label
                            }))}
                            value={consumer.occupation}
                            onChangeFunctions={[(newValue) => setConsumer({...consumer, occupation: newValue })]}
                          />
                        </>
                      }
                    </div>
                    <button type="submit" className="c-btn__01">入力内容の確認</button>
                  </form>
                </div>
              </div>
              <SidebarNavigation items={[{ id: "id-01", label: "お客様情報" }, { id: "id-02", label: "申込者情報" }, { id: "id-03", label: "使用者情報" }]} />
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
}

export default Registration;